// Read normalization + pattern-aware confusable correction (spec §6.2–6.3).

import { PATTERNS, charMatches, type PlatePattern } from './patterns';

/** OCR confusables, applied only when the position prior demands the other class. */
const TO_DIGIT: Record<string, string> = { O: '0', I: '1', B: '8', S: '5', Z: '2' };
const TO_LETTER: Record<string, string> = { '0': 'O', '1': 'I', '8': 'B', '5': 'S', '2': 'Z' };

/** Uppercase and strip separators/whitespace. */
export function normalize(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export interface PatternMatch {
  patternId: string;
  /** corrected text after confusable fixes */
  text: string;
  template: string;
  fallback: boolean;
}

/** Try to correct `s` to fit `template`; null when impossible. */
export function correctToTemplate(s: string, template: string): string | null {
  if (s.length !== template.length) return null;
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i] as string;
    const cls = template[i] as 'D' | 'A' | 'X';
    if (charMatches(ch, cls)) {
      out += ch;
      continue;
    }
    const swapped = cls === 'D' ? TO_DIGIT[ch] : cls === 'A' ? TO_LETTER[ch] : undefined;
    if (swapped === undefined) return null;
    out += swapped;
  }
  return out;
}

/**
 * Match a normalized read against the pattern set in priority order.
 * Prefers a template needing the fewest confusable swaps within each pattern.
 */
export function matchPattern(
  s: string,
  patterns: readonly PlatePattern[] = PATTERNS,
): PatternMatch | null {
  if (s.length < 4 || s.length > 9) return null;
  for (const p of patterns) {
    let best: { text: string; template: string; swaps: number } | null = null;
    for (const template of p.templates) {
      const corrected = correctToTemplate(s, template);
      if (corrected === null) continue;
      let swaps = 0;
      for (let i = 0; i < s.length; i++) if (corrected[i] !== s[i]) swaps++;
      if (!best || swaps < best.swaps) best = { text: corrected, template, swaps };
    }
    if (best) {
      return {
        patternId: p.id,
        text: best.text,
        template: best.template,
        fallback: p.fallback === true,
      };
    }
  }
  return null;
}
